import Vue from 'vue'
import { Component, Watch, Prop, Provide } from 'vue-property-decorator'
import { Mixins } from 'vue-mixin-decorator'
import ProjectMixin from '@/_mixins/project'
import UserMixin from '@/_mixins/user'
import { getRequest, postRequest, deleteRequest, patchRequest } from '@/plugins/api-axios'
import AppToolbar from '@/_components/bar/appToolbar/appToolbar.vue'
import AutocompleteCompany from '@/components/Autocomplete/company.vue'
import AutocompleteUser from '@/components/Autocomplete/User/User.vue'
import { openDialog } from '@/_components/dialog/dialog'
import PhoneInput from '@/_components/input/phone/phone.vue'
import ConfirmationDialog from './components/confirmationDialog.vue'
import StartBankIcon from './components/startBankIcon.vue'
import DocumentItem from './components/document.vue'
import UserSearch from './components/userSearch.vue'
import RiskTags from './components/riskTags.vue'
import CompanyTree from './components/companyTreeX.vue'
import RiskAssessmentFormResponseDialog from './components/Form/riskAssessmentFormResponseDialog.vue'
import FormResponseInviteDialog from './components/Form/formResponseInviteDialog.vue'
import FormResponseViewerDialog from './components/Form/formResponseViewerDialog.vue'
import ActivityLogsDialog from './components/Log/activityLogsDialog.vue'
import CompanyActivityLogsDialog from './components/Log/CompanyActivityLogsDialog.vue'
import AlertLogDialog from './components/workAlert/log/dialog.vue'
import DialogTemplate from '@/_components/dialog/template/template.vue'
import { deattribute, deserialise, serialise } from 'kitsu-core'
import Comments from '@/views/Project/Comment/views/Comments/Comments.vue'
import RiskLevelMixin from './mixins/risk.mixin'
import CompanyInfoMixin from '@/_mixins/companyInfo.mixin'
import FormResponseMixin from './mixins/formResponse.mixin'
import RichTextField from '@/components/RichTextField/richTextField.vue'
import UserAvatar from './components/userAvatar.vue'
import UserListItem from './components/userListItem.vue'
import WorkAlertSettings from '@/views/Project/ch/components/workAlert/index.vue'
import Insights from '@/views/Project/ch/components/Insights/insights.vue'
import ContractorUsers from '@/views/Project/ch/components/contractorUsers.vue'
import LogMixin from '@/_mixins/log'
import type MixinInterface from '@/_mixins'
import type {
    User,
    Contact,
    Document,
    TreeViewItem,
    DocumentTag,
    ValidationRule,
} from './contractors.types'
import { Debounce } from 'vue-debounce-decorator'
import { RawLocation } from 'vue-router'
import ContractorRolesDialog from '@/views/Authorization/components/userRow/ContractorRolesDialog.vue'
import Contractors from '@/views/Project/ch/components/contractors.vue'
import ContractorsPreviewDialog from '@/views/Project/ch/components/contractorsPreviewDialog.vue'
import Draggable from 'vuedraggable'
import AuthorizationMixin from '@/_mixins/authorization'
import { getURL } from '@/utils/axios'
import { ContractorRole } from '@/views/Project/types/contractor'
import VbitStatusBanner from '@/components/banner/vbitStatus.vue'
import SaveStatusIndicator from '@/components/saveStatusIndicator.vue'

let targetDropZone
const SystemContractorAdminRole = 'system.contractorAdmin'

@Component({
    mixins: [
        AuthorizationMixin,
        RiskLevelMixin,
        CompanyInfoMixin,
        FormResponseMixin,
        LogMixin,
        ProjectMixin,
        UserMixin,
    ],
    components: {
        AppToolbar,
        AutocompleteCompany,
        AutocompleteUser,
        Contractors,
        ContractorUsers,
        Comments,
        CompanyTree,
        DocumentItem,
        DialogTemplate,
        Draggable,
        Insights,
        PhoneInput,
        RiskTags,
        RichTextField,
        StartBankIcon,
        UserSearch,
        RiskAssessmentFormResponseDialog,
        UserAvatar,
        UserListItem,
        FormResponseInviteDialog,
        FormResponseViewerDialog,
        WorkAlertSettings,
        AlertLogDialog,
        VbitStatusBanner,
        SaveStatusIndicator,
    },
})
export default class CompanyHierarchy extends Mixins<
    MixinInterface & CompanyInfoMixin & AuthorizationMixin
>(ProjectMixin, UserMixin) {
    @Prop() companyId!: string

    companyList = {
        isPending: false,
        newCompany: null as Maybe<{ id: string }>,
        tree: undefined as undefined | TreeViewItem[],
        includeArchive: false,
        hasUnreadWorkAlert: false,
        searchTerm: '',
        riskScores: [] as string[],
    }

    @Provide('hierarchyProvider') hierarchyProvider = {
        addCompany: this.addCompany,
        updateCompany: this.updateCompany,
    }

    hasError = false
    hierarchy: any = null
    projectSettings: any = null
    systemCompanyAdminRole: any = null
    movingList: any = []
    worker: Worker | null = null
    accessible = false
    startDateMenu = false
    endDateMenu = false
    startAndEndDateFormValid = true

    isoDate(value: Maybe<string>, format = 'YYYY.MM.DD') {
        if (!value) return null
        return this.formatDate(value, this.getDeviceTimeZone, format)
    }

    toIsoDate(value: Maybe<string>) {
        if (!value) return null
        return this.formatDateTimeWithTimeZone(value, '00:00', this.getDeviceTimeZone)
    }

    toggleDateMenu(menu: 'startDateMenu' | 'endDateMenu', value: boolean) {
        setTimeout(() => {
            this[menu] = value
        }, 0)
    }

    cancelMove(item) {
        item.id = Math.abs(item.id)
        item._isMoving = false
        this.movingList.pop()
    }

    cloneItem(item) {
        return { ...item, id: Math.abs(Number(item.id)), _isMoving: false }
    }

    countSubContractors(node) {
        let count = 0

        if (node.children) {
            count += node.children.filter((node) => !node._isMoving).length
            node.children.forEach((child) => {
                count += this.countSubContractors(child)
            })
        }

        return count
    }

    handleMove(event) {
        const parentEl = event.to.closest('[data-id]')

        // target parent element does not allow children
        if (parentEl && !parentEl.dataset.isAvailableParent) {
            return false
        }

        let parentId = parentEl?.dataset.id || null

        // only contractor admin can move companies to top level
        if (!parentId && !this.isAdmin) {
            return false
        }

        return true
    }

    async handleDragEnd(event) {
        let { item, to, newIndex } = event
        let parentEl = to.closest('[data-id]')
        let parentId = parentEl?.dataset.id || null

        this.updateCompany({ id: Math.abs(item.dataset.id), index: newIndex, parentId })
    }

    handleAddContractorToggle() {
        this.addNewContractorToggle = !this.addNewContractorToggle

        if (!this.addNewContractorToggle) {
            this.companyList.newCompany = null
        }
    }

    handleSearchContractorToggle() {
        this.searchContractorToggle = !this.searchContractorToggle
        if (!this.searchContractorToggle && this.companyList.searchTerm) {
            this.companyList.searchTerm = ''
            this.filterContractors()
        }
    }

    selectContractor(contractors: any[]) {
        const [item] = contractors

        if (
            !item ||
            !item.constraints.companyDetails?.authorized ||
            this.selectedCompany?.id === item.id
        ) {
            return
        }

        this.$router.push({
            name: this.currentGroup ? 'group-contractors' : 'project-contractors',
            params: {
                companyId: item.id,
                projectId: this.currentProject.id,
            },
        })
    }

    selectedCompany = null as any
    companyDetails = {
        isCommentsDialogOpen: false,
        riskAssessments: [] as any[],
        intakeAssessments: [] as any[],
        checkingFormResponses: false,
        formRules: {} as Record<string, ValidationRule[]>,
    }

    contactList = {
        isPending: false,
        isFormValid: false,
        formRules: {} as Record<string, ValidationRule[]>,
    }
    contact = this.newContact()

    documentList = {
        isPending: false,
        isUploading: false,
        isFormValid: false,
        formRules: {} as Record<string, ValidationRule[]>,
    }
    document = this.newDocument()
    selectedFile: Maybe<File> = null

    companyByNodeId = {}
    nodeByCompanyDetails = {}
    nodeByCompany = {}
    companyUnreadWorkAlert = null as null | Record<string, boolean>
    isDocumentDialogOpen = false
    isContactDialogOpen = false
    isRiskFormResponseDialogVisible = false
    isFormResponseInviteDialogVisible = false
    alertLogDialog = false
    searchContractorToggle = false
    addNewContractorToggle = false
    saveStatus: 'saving' | 'saved' | null = null
    contractorRoles: ContractorRole[] = []

    get isPersonnelListModuleActive() {
        return this.isModuleActive('personnel')
    }

    get canImportContractors() {
        return this.hierarchy?.canImportFromBklr === true
    }

    get moduleActive() {
        let currentProjectModules = this.activeModules
            .filter(
                (module: any) => module.relationships.project.data.id === this.currentProject.id
            )
            .map((module: any) => module.relationships.module.data.id)

        return currentProjectModules.includes('contractor')
    }

    get deviceTimeZone() {
        return this.guessTimezone()
    }

    get isAdmin() {
        return this.hasPermission(this.allPermissions, 'contractor.admin')
    }

    get hasManageAccess() {
        return this.hasPermission(this.allPermissions, 'authorization.manage_access')
    }

    get hasDelegateAccess() {
        return this.hasPermission(this.allPermissions, 'authorization.delegate_access')
    }

    get companyAdminWithWriteAccess() {
        return this.hasPermission(this.allPermissions, 'contractor.companyAdmin_write')
    }

    get canManageContacts() {
        return this.selectedCompany?.companyDetails?.data?.constraints?.contacts?.writable
    }

    get canManageDocuments() {
        return this.selectedCompany?.companyDetails?.data?.constraints?.documents?.writable
    }

    get contactDescriptionMaxLength() {
        return 50
    }

    get documentNameMaxLength() {
        return 50
    }

    get riskItems() {
        return [
            {
                text: this.$t('fe_contractors["projectCompanyDetails.risk.none"]'),
                value: null,
            },
            ...['low', 'medium', 'high'].map((risk) => ({
                text: this.$t(`contractor_enums["companyDetails.risk.${risk}"]`),
                value: risk,
                // @ts-ignore -- mixinRiskLevelColor is defined in RiskLevelMixin
                iconColor: this.mixinRiskLevelColor[risk],
            })),
        ]
    }

    get allowedFileTypes() {
        let fileMetadata = this.$store.getters.attachmentConstraints.maxBytes
            ? this.$store.getters.attachmentConstraints
            : this.getFileRequirements()
        return fileMetadata?.allowedExtensions
            ? `.${fileMetadata?.allowedExtensions.join(',.')}`
            : ''
    }

    @Watch('companyId')
    async handleRouteCompanyIdChanged(_, oldId) {
        document
            .querySelector('[data-id="' + this.nodeByCompanyDetails[oldId] + '"] button')
            ?.classList.remove('active')
        await new Promise((resolve) => setTimeout(resolve, 300))
        this.setSelectedCompany()
    }

    get companyBreadcrumbItems() {
        if (!this.selectedCompany) return []
        let hierarchy: { text: string; to?: RawLocation }[] = []
        let parent = this.selectedCompany.parent.data
        while (parent && parent.company?.data) {
            if (parent.companyDetails?.data?.id) {
                hierarchy.unshift({
                    text: parent.company.data.name,
                    to: {
                        name: this.currentGroup ? 'group-contractors' : 'project-contractors',
                        params: {
                            companyId: parent.companyDetails.data.id,
                            projectId: this.currentProject.id,
                        },
                    },
                })
            }
            parent = parent.parent.data
        }
        if (hierarchy.length) hierarchy.push({ text: '' })
        return hierarchy
    }

    get selectedCompanyContacts() {
        if (!Array.isArray(this.selectedCompany?.companyDetails?.data?.contacts?.data)) return []
        return this.selectedCompany?.companyDetails?.data?.contacts?.data
    }

    get allCompanyDocuments() {
        if (!Array.isArray(this.selectedCompany?.companyDetails?.data?.documents?.data)) return []
        if (this.isAdmin) return this.selectedCompany?.companyDetails?.data?.documents?.data
        return this.selectedCompany?.companyDetails?.data?.documents?.data?.filter(
            (doc) => doc.isVisibleToCompanyAdmins
        )
    }

    get selectedCompanyDocuments() {
        return this.allCompanyDocuments.filter((doc) => doc.tag !== 'tariff' && doc.tag !== 'risk')
    }

    get tariffDocuments() {
        return this.allCompanyDocuments.filter((doc) => doc.tag === 'tariff')
    }

    get riskDocuments() {
        return this.allCompanyDocuments.filter((doc) => doc.tag === 'risk')
    }

    get helpTextContent() {
        if (
            this.projectSettings?.helpText &&
            Object.keys(this.projectSettings.helpText).length > 0
        ) {
            const [languageKey] = Object.keys(this.projectSettings.helpText)
            return (
                this.projectSettings.helpText[this.$store.getters.user.attributes.language] ||
                this.projectSettings.helpText[languageKey]
            )
        }
        return null
    }

    get hasUnreadWorkAlert() {
        return this.companyUnreadWorkAlert?.[this.selectedCompany.companyDetails?.data?.id] || false
    }

    async setSelectedCompany() {
        const id = this.nodeByCompanyDetails[this.companyId] || this.nodeByCompany[this.companyId]
        // id can be companyDetails id or node id (8693quvy4)
        this.selectedCompany = this.companyByNodeId[id] || this.companyByNodeId[this.companyId]
    }

    @Watch('selectedCompany')
    setCompanyDetailsData() {
        this.companyDetails.riskAssessments = []
        this.nodeAdmins = []
        this.searchUsers = []
        this.searchTerm = ''

        if (!this.selectedCompany) return

        this.nodeAdmins = this.selectedCompany.admins?.data || []

        this.getNodeAdmins()
        this.checkFormResponses()
        this.getCompanyDetails()
        // scroll details into view
        ;(this.$refs.companyDetails as any)?.$el?.scrollIntoView({ block: 'end' })
    }

    companyRiskData(item: any) {
        let data = item.companyDetails?.data?.startBankData

        if (!data || !data.risk) return null

        return data
    }

    handleDrop(event) {
        let el = this.$refs.dropContainer as HTMLElement
        if (el) el.dataset.dragOver = 'false'
        if (event.dataTransfer.files.length > 1) return
        this.setSelectedFile(event.dataTransfer.files[0])
    }

    handleDragEnter(event: DragEvent) {
        targetDropZone = event.target
        let el = this.$refs.dropContainer as HTMLElement
        if (el) el.dataset.dragOver = 'true'
    }

    handleDragLeave(event: DragEvent) {
        let el = this.$refs.dropContainer as HTMLElement
        if (el && event.target == targetDropZone) el.dataset.dragOver = 'false'
    }

    async setSelectedFile(file: Maybe<File>) {
        if ((this.document.constraints && !this.document.constraints?.fileId?.writable) || !file)
            return

        if (!this.document.name) this.document.name = file.name
        this.documentList.isUploading = true
        this.document.fileId = await this.postFile(file)
        this.documentList.isUploading = false
    }

    @Watch('currentProjectId')
    handleProjectIdChange() {
        this.companyList.tree = undefined
        this.getCompanyHierarchy()
    }

    roles: any[] = []
    async getNodeAdmins() {
        if (!this.currentProjectId || !this.selectedCompany) return

        if (this.roles.length === 0) {
            await this.getRoles()
        }

        this.systemCompanyAdminRole = this.roles.find(
            (role) => role.kind === SystemContractorAdminRole
        )

        if (!this.systemCompanyAdminRole) return

        const { data } = await getRequest(
            this.getURL({
                path: '/projectUsers',
                base: this.url_authorization,
                params: {
                    'filter[userKindIfConfirmed]': 'normal',
                    'filter[contractors][contains]': this.selectedCompany.id,
                    'filter[rolesIncludingContractor][contains]': this.systemCompanyAdminRole._id,
                    include:
                        'user,contractors,contractorAssignments,contractorAssignments.contractor,contractorAssignments.roleAssignments,contractorAssignments.roleAssignments.role,displayContractor,displayContractor.company',
                },
            })
        )
        this.nodeAdmins = deserialise(data).data
    }

    async getCompanyDetails() {
        if (
            !this.selectedCompany ||
            !this.selectedCompany.companyDetails?.data.constraints.workAlertDefinitions.authorized
        ) {
            return
        }

        const { data } = await getRequest(
            this.getURL({
                path: `companyDetails/${this.selectedCompany.companyDetails?.data?.id}`,
                base: this.contractorBaseUrl,
                params: {
                    include: 'contacts,mainContact,documents,workAlertDefinitions',
                    skipLinks: '',
                },
            })
        )
        // The lastOnSite date is not returned in the response, so we need to merge it with the existing data
        Object.assign(this.selectedCompany.companyDetails.data, deserialise(data).data)
    }

    @Debounce(500)
    handleContractorSearchTermChange() {
        this.filterContractors()
    }

    @Watch('companyList.includeArchive')
    @Watch('companyList.riskScores')
    @Watch('companyList.hasUnreadWorkAlert')
    @Watch('filteredContractorRoles')
    handleFilterChange() {
        this.filterContractors()
    }

    filterContractors(tree = this.companyList.tree) {
        this.worker?.postMessage({
            type: 'filterContractor',
            payload: {
                tree,
                includeArchive: this.companyList.includeArchive,
                searchTerm: this.companyList.searchTerm,
                riskScores: this.companyList.riskScores,
                hasUnreadWorkAlert: this.companyList.hasUnreadWorkAlert,
                unreadWorkAlert: this.companyUnreadWorkAlert || {},
                roles: this.filteredContractorRoles,
            },
        })
    }

    filterContractorsByRisk(riskScore: string) {
        if (this.companyList.riskScores.includes(riskScore)) {
            this.companyList.riskScores = this.companyList.riskScores.filter(
                (score) => score !== riskScore
            )
        } else {
            this.companyList.riskScores.push(riskScore)
        }
    }

    async reloadContractors() {
        const tree = await this.getHierarchyTree()
        this.filterContractors(tree)
    }

    async getHierarchyTree() {
        let [{ data }, { data: nodeCompanyDetailsData }] = await Promise.all([
            getRequest(
                this.getURL({
                    path: `contractorHierarchies/${this.currentProjectId}`,
                    base: this.contractorBaseUrl,
                    params: {
                        include:
                            'nodes,nodes.parent,nodes.company,nodes.companyDetails,nodes.companyDetails.contacts,nodes.companyDetails.mainContact,nodes.companyDetails.documents,nodes.roles',
                        skipLinks: '',
                    },
                })
            ),
            getRequest(
                this.getURL({
                    path: `contractorHierarchies/${this.currentProjectId}`,
                    base: this.contractorBaseUrl,
                    params: {
                        include:
                            'nodes.companyDetails&fields[contractorHierarchy]=id,nodes&fields[companyDetails]=lastOnSite',
                        skipLinks: '',
                    },
                })
            ),
        ])
        const nodeByHierarchyNodeId = deserialise(nodeCompanyDetailsData).data.nodes.data.reduce(
            (acc, node) => Object.assign(acc, { [node.id]: node }),
            {}
        )

        this.setHierarchy({ ...data.data })

        const hierarchyData = deserialise(data)
        const ids = {}
        const tree: any[] = []
        hierarchyData.data.nodes?.data.forEach((node) => {
            if (node.companyDetails?.data) {
                Object.assign(
                    node.companyDetails.data,
                    nodeByHierarchyNodeId[node.id].companyDetails.data
                )
            }
            ids[node.id] = Object.assign({ children: [] }, node)
            if (node.companyDetails?.data) {
                this.nodeByCompanyDetails[node.companyDetails.data.id] = node.id
            }
            if (node.company?.data) {
                this.nodeByCompany[node.company.data.id] = node.id
            }
        })
        hierarchyData.data.nodes?.data.forEach((node) => {
            const addingNode = ids[node.id]
            addingNode._isMatch = !addingNode.isArchived
            if (!node.parent.data) {
                tree.push(addingNode)
            } else {
                ids[node.parent.data.id].children.push(addingNode)
                ids[node.parent.data.id].children.sort((a, b) => a.index - b.index)
            }
        })
        this.companyByNodeId = ids
        this.setSelectedCompany()

        return tree
    }

    async getCompanyHierarchy() {
        if (!this.currentProjectId) return

        try {
            this.hasError = false
            this.companyList.tree = await this.getHierarchyTree()
        } catch (e) {
            console.log(e)
            this.hasError = true
        }
    }

    setHierarchy(data: any) {
        if (data) this.hierarchy = deattribute(data)
    }

    startMoving(item) {
        item._isMoving = true // mark item as moving
        item.id = -Number(item.id)
        this.movingList.push(item)
    }

    async addCompany(company, parentId?) {
        if (this.companyList.isPending) return

        this.companyList.isPending = true
        let payload = serialise('contractorHierarchyNode', {
            hierarchy: {
                data: { type: 'contractorHierarchy', id: this.hierarchy.id },
            },
            company: {
                data: { type: 'company', id: company.id },
            },
            ...(parentId && {
                parent: {
                    data: { type: 'contractorHierarchyNode', id: parentId },
                },
            }),
        })

        await postRequest(
            this.getURL({ path: 'contractorHierarchyNodes', base: this.contractorBaseUrl }),
            this.headersmixins,
            payload.data
        )
        this.reloadContractors()

        this.companyList.newCompany = null
        this.companyList.isPending = false
        this.addContractorMenu = false
    }

    async importContractors() {
        const confirm = await openDialog(ContractorsPreviewDialog, {
            hierarchyId: this.hierarchy.id,
        })
        if (confirm) this.reloadContractors()
    }

    async updateCompany(attrs: Record<string, any>) {
        if (!attrs.id) return

        let { parentId, ...data } = attrs
        if (typeof parentId !== 'undefined') {
            data.parent = {
                data: parentId ? { type: 'contractorHierarchyNode', id: parentId } : null,
            }
        }
        let payload = serialise('contractorHierarchyNode', data)

        await patchRequest(
            this.getURL({
                path: `contractorHierarchyNodes/${data.id}`,
                base: this.contractorBaseUrl,
                params: {
                    include:
                        'company,companyDetails,companyDetails.contacts,companyDetails.mainContact,companyDetails.documents,roles',
                },
            }),
            this.headersmixins,
            payload.data
        )
        this.reloadContractors()

        // if current selected company is archived, remove it from the url
        if (data.isArchived && data.id === this.companyId && !this.companyList.includeArchive) {
            this.$router.replace({
                name: this.$route.name as string,
                params: { projectId: this.currentProjectId as string },
            })
        }
    }

    @Debounce(500)
    debounceUpdateCompanyDetails() {
        this.updateCompanyDetails()
    }

    async updateStartOrEndDate() {
        await this.$nextTick()
        const isFormValid = await (this.$refs.companyDetailsDateForm as any).validate()

        if (isFormValid) {
            this.updateCompanyDetails()
        }
    }

    async updateCompanyDetails() {
        if (!this.selectedCompany) return

        const payload = serialise('companyDetails', {
            id: this.selectedCompany.companyDetails?.data.id,
            startDate: this.selectedCompany.companyDetails?.data.startDate,
            endDate: this.selectedCompany.companyDetails?.data.endDate,
            risk: this.selectedCompany.companyDetails?.data.risk,
            description: this.selectedCompany.companyDetails?.data.description,
            tariffDescription: this.selectedCompany.companyDetails?.data.tariffDescription,
        })

        this.saveStatus = 'saving'
        setTimeout(() => {
            this.saveStatus = null
        }, 2500)

        await patchRequest(
            this.getURL({
                path: `companyDetails/${payload.data.id}`,
                base: this.contractorBaseUrl,
            }),
            this.headersmixins,
            payload.data
        )
        this.getCompanyDetails()
        this.saveStatus = 'saved'
    }

    newContact(contact?: Contact): Contact {
        const contactItem = Object.assign(
            {
                name: '',
                email: null,
                phone: null,
                description: null,
                isPrimary:
                    contact &&
                    this.selectedCompany?.companyDetails?.data?.mainContact?.data?.id ===
                        contact?.id,
            },
            contact
        )

        return contactItem
    }

    setContact(data: string | User) {
        // coming from user autocomplete
        if (typeof data === 'string') {
            this.contact.name = data
        } else {
            this.contact.name = data?.attributes?.name
            this.contact.email = data?.attributes?.primaryEmail
            this.contact.phone = data?.attributes?.mobileNumber
        }
    }

    openContactDialog(contact: Contact) {
        this.contact = contact
        this.setContactDialogOpen(true)
    }

    async updateContact() {
        if (!this.contactList.isFormValid || this.contactList.isPending) {
            return
        }

        this.contactList.isPending = true
        let payload = serialise('companyContact', {
            id: this.contact.id,
            name: this.contact.name || null,
            email: this.contact.email || null,
            description: this.contact.description || null,
            phone: this.contact.phone || null,
        })

        const url = this.getURL({
            path: `companyContacts/${this.contact.id}`,
            base: this.contractorBaseUrl,
        })

        try {
            const { data } = await patchRequest(url, this.headersmixins, payload.data)
            const contactIndex = this.selectedCompany.companyDetails.data.contacts.data.findIndex(
                (d) => d.id === this.contact.id
            )
            this.selectedCompany.companyDetails.data.contacts.data[contactIndex] = deattribute(
                data.data
            )
            const mainContact = this.selectedCompany.companyDetails.data.mainContact?.data
            if (this.contact.isPrimary && mainContact?.id !== this.contact.id) {
                await this.setMainContact(
                    this.selectedCompany.companyDetails.data.contacts.data[contactIndex]
                )
            } else if (!this.contact.isPrimary && mainContact?.id === this.contact.id) {
                await this.setMainContact(null)
            }
            this.isContactDialogOpen = false
        } catch (error) {
            this.$store.commit('setErrorSnackBar', {
                data: error.data.response.data.errors,
                text: this.$t('vbit_error.default_header'),
                url: `PATCH: ${url}`,
            })
        } finally {
            this.contactList.isPending = false
        }
    }

    async addContact() {
        if (!this.contactList.isFormValid || this.contactList.isPending) {
            return
        }

        this.contactList.isPending = true
        let payload = serialise('companyContact', {
            name: this.contact.name || null,
            email: this.contact.email || null,
            description: this.contact.description || null,
            phone: this.contact.phone || null,
            companyDetails: {
                data: { type: 'companyDetails', id: this.selectedCompany.companyDetails?.data?.id },
            },
        })

        const url = this.getURL({
            path: 'companyContacts',
            base: this.contractorBaseUrl,
        })

        try {
            const { data } = await postRequest(url, this.headersmixins, payload.data)
            const newContact = deattribute(data.data) as Contact
            if (this.contact.isPrimary) {
                await this.setMainContact(newContact)
            }
            this.selectedCompany.companyDetails.data.contacts.data.push(newContact)
            this.isContactDialogOpen = false
        } catch (error) {
            this.$store.commit('setErrorSnackBar', {
                data: error.data.response.data.errors,
                text: this.$t('vbit_error.default_header'),
                url: `POST: ${url}`,
            })
        } finally {
            this.contactList.isPending = false
        }
    }

    async deleteContact(contact: Contact) {
        const props: Record<string, string> = {
            message: 'fe_contractors["projectCompanyContact.delete_confirmation"]',
            title: this.$t(
                'fe_contractors["projectCompanyContact.heading_delete_contact"]'
            ).toString(),
            name: contact.name,
        }

        if (contact.id === this.selectedCompany.companyDetails.data.mainContact?.data?.id) {
            props.description = this.$t(
                'fe_contractors["projectCompanyContact.delete_helpText"]'
            ).toString()
        }

        let confirm = await openDialog(ConfirmationDialog, props)

        if (confirm) {
            this.contactList.isPending = true

            await deleteRequest(
                this.getURL({
                    path: `companyContacts/${contact.id}`,
                    base: this.contractorBaseUrl,
                })
            )
            this.getCompanyDetails()

            this.contactList.isPending = false
        }
    }

    openDialogToAddMainContact() {
        const newContact = this.newContact()
        newContact.isPrimary = true
        this.openContactDialog(newContact)
        this.$nextTick(() => {
            ;(this.$refs.contacts as any)?.$el?.scrollIntoView()
        })
    }

    async setMainContact(contact: Contact | null) {
        await patchRequest(
            this.getURL({
                path: `companyDetails/${this.selectedCompany.companyDetails.data.id}`,
                base: this.contractorBaseUrl,
            }),
            this.headersmixins,
            serialise('companyDetails', {
                id: this.selectedCompany.companyDetails.data.id,
                mainContact: {
                    data: contact ? { type: 'companyContact', id: contact.id } : null,
                },
            }).data
        )
        this.selectedCompany.companyDetails.data.mainContact = { data: contact }
    }

    selectFile() {
        let fileInputRefs = (this.$refs.fileInputRef as Vue).$refs
        if (fileInputRefs) {
            let inputEl = fileInputRefs.input as HTMLInputElement
            inputEl?.click()
        }
    }

    async updateDocument() {
        if (!this.documentList.isFormValid || this.documentList.isPending) return

        this.documentList.isPending = true
        let payload = serialise('companyDocument', {
            id: this.document.id,
            name: this.document.name,
            description: this.document.description,
            isVisibleToCompanyAdmins: this.document.isVisibleToCompanyAdmins,
            fileId: this.document.fileId,
        })

        let { data } = await patchRequest(
            this.getURL({
                path: `companyDocuments/${this.document.id}`,
                base: this.contractorBaseUrl,
            }),
            this.headersmixins,
            payload.data
        )

        this.selectedCompany.companyDetails.data.documents.data = [
            ...this.selectedCompany.companyDetails.data.documents.data.filter(
                (d) => d.id !== this.document.id
            ),
            deattribute(data.data),
        ]
        this.closeDocumentDialog()
        this.documentList.isPending = false
    }

    async addDocument() {
        if (!this.documentList.isFormValid || this.documentList.isPending) return

        this.documentList.isPending = true
        let payload = serialise('companyDocument', {
            name: this.document.name,
            description: this.document.description,
            fileId: this.document.fileId,
            tag: this.document.tag,
            isVisibleToCompanyAdmins: this.document.isVisibleToCompanyAdmins,
            companyDetails: {
                data: { type: 'companyDetails', id: this.selectedCompany.companyDetails.data.id },
            },
        })

        let { data } = await postRequest(
            this.getURL({
                path: `companyDocuments`,
                base: this.contractorBaseUrl,
            }),
            this.headersmixins,
            payload.data
        )

        this.selectedCompany.companyDetails.data.documents.data.push(deattribute(data.data))
        this.closeDocumentDialog()
        this.documentList.isPending = false
    }

    async deleteDocument(document: Document) {
        if (this.documentList.isPending) return

        let ok = await openDialog(ConfirmationDialog, {
            message: 'fe_contractors["projectCompanyDocument.delete_confirmation"]',
            title: this.$t('fe_contractors["projectCompanyDocument.heading_delete_document"]'),
            name: document.name,
        })

        if (!ok) return

        this.documentList.isPending = true
        await deleteRequest(
            this.getURL({
                path: `companyDocuments/${document.id}`,
                base: this.contractorBaseUrl,
            })
        )
        this.getCompanyDetails()
        this.selectedCompany.companyDetails.data.documents.data =
            this.selectedCompany.companyDetails.data.documents.data.filter(
                (item) => item.id !== document.id
            )
        this.documentList.isPending = false
    }

    newDocument(document?: Document, tag?: DocumentTag): Document {
        return Object.assign(
            {
                name: '',
                fileId: '',
                description: null,
                tag: tag ?? null,
                isVisibleToCompanyAdmins: this.companyAdminWithWriteAccess && !this.isAdmin,
            },
            document
        )
    }

    addOrEditDocument(document: Document) {
        this.document = document
        this.isDocumentDialogOpen = true
    }

    closeDocumentDialog() {
        this.isDocumentDialogOpen = false
        this.setSelectedFile(null)
    }

    setContactDialogOpen(open: boolean) {
        this.isContactDialogOpen = open
    }

    showActivityLogs() {
        openDialog(ActivityLogsDialog)
    }

    showCompanyAlertLogs() {
        openDialog(CompanyActivityLogsDialog, {
            companyName: this.selectedCompany.company.data.name,
            id: this.selectedCompany.companyDetails.data.id,
        })
    }

    showAlertLogs() {
        // when switch between nodes, wait for the *selectedCompany* to be updated
        setTimeout(async () => {
            this.alertLogDialog = true
        }, 50)
    }

    hideAlertLogs({ hasAlertMarkedAsRead, goToManage }) {
        this.alertLogDialog = false
        if (hasAlertMarkedAsRead) {
            this.getUnreadWorkAlert()
        }
        if (goToManage) {
            ;(this.$refs.workAlertRef as Vue)?.$el?.scrollIntoView()
        }
    }

    handleUpdateBellIconStatus({ hasAlertMarkedAsRead }) {
        if (hasAlertMarkedAsRead) {
            this.getUnreadWorkAlert()
        }
    }

    async handleRiskFormResponseDialogClosed(data: { refresh: boolean }) {
        // wait for Form/Contractor BE services to finish processing the form response
        // in order to get the correct Risk score
        this.isRiskFormResponseDialogVisible = false
        data?.refresh && setTimeout(this.reloadContractors, 500)
    }

    handleFormResponseViewerDialogClosed(data: { refresh: boolean }) {
        this.isFormResponseViewerDialogVisible = false
        data?.refresh && this.checkFormResponses()
    }

    handleFormResponseInviteDialogClosed(data: { refresh: boolean }) {
        this.isFormResponseInviteDialogVisible = false
        data?.refresh && this.checkFormResponses()
    }

    async checkFormResponses() {
        this.isRiskFormResponseDialogVisible = false

        try {
            this.companyDetails.checkingFormResponses = true
            let { data: contractorFormResponses } = await getRequest(
                `${this.contractorBaseUrl}/contractorFormResponses`,
                this.headersmixins,
                {
                    'filter[company]': this.selectedCompany.company.data.id,
                    'filter[form.project]': this.currentProjectId,
                    'page[limit]': 100,
                    include: 'form,readAccess,writeAccess,submittedBy',
                }
            )
            let responses = deserialise(contractorFormResponses).data
            this.companyDetails.riskAssessments = responses
                .filter(
                    (response) =>
                        response.form.data.kind === 'riskAssessment' && response.status === 'done'
                )
                .sort((a, b) => {
                    return new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
                })

            this.companyDetails.intakeAssessments = responses
                .filter((response) => response.form.data.kind !== 'riskAssessment')
                .sort((a, b) => {
                    return new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
                })
        } finally {
            this.companyDetails.checkingFormResponses = false
        }
    }

    isFormResponseViewerDialogVisible = false
    contractorFormResponseId: null | string = null
    contractorFormResponse: any = null
    viewSubmission(contractorFormResponse: any) {
        if (!contractorFormResponse.form.data?.companyAdminsCanViewResponses && !this.isAdmin)
            return

        this.isFormResponseViewerDialogVisible = true
        this.contractorFormResponseId = contractorFormResponse.id
        this.contractorFormResponse = contractorFormResponse
    }

    isSearchingProjectUsers = false
    searchUsers: any[] = []
    searchTerm = ''
    nodeAdmins: any[] = []

    async updateNodeAdmin(user: any, adding: boolean) {
        let contractorAssignment = user.contractorAssignments.data.find(
            (item) => item?.contractor.data.id === this.selectedCompany.id
        )

        if (adding) {
            const contractorAssignmentPayload = {
                projectUser: {
                    data: { type: 'projectUser', id: user.id },
                },
                contractor: {
                    data: { type: 'contractor', id: this.selectedCompany.id },
                },
            }

            if (!contractorAssignment) {
                const contractorAssignmentDataResponse = await postRequest(
                    this.getURL({
                        path: `/projectUserContractorAssignments`,
                        base: this.url_authorization,
                        params: {
                            include: 'roleAssignments,roleAssignments.role,contractor',
                        },
                    }),
                    {},
                    serialise('projectUserContractorAssignment', contractorAssignmentPayload).data
                )
                contractorAssignment = deserialise(contractorAssignmentDataResponse.data).data
            }

            const roleAssignmentPayload = {
                includeSubcontractors: true,
                contractorAssignment: {
                    data: { type: 'projectUserContractorAssignment', id: contractorAssignment.id },
                },
                role: {
                    data: { type: 'role', id: this.systemCompanyAdminRole._id },
                },
            }
            postRequest(
                this.getURL({
                    path: `/projectUserContractorRoleAssignments`,
                    base: this.url_authorization,
                }),
                {},
                serialise('projectUserContractorRoleAssignment', roleAssignmentPayload).data
            )
        } else {
            contractorAssignment = user.contractorAssignments.data.find(
                (item) => item.contractor.data.id === this.selectedCompany.id
            )

            if (!contractorAssignment || !contractorAssignment.canDelete) {
                return
            }

            deleteRequest(
                this.getURL({
                    path: `/projectUserContractorAssignments/${contractorAssignment.id}`,
                    base: this.url_authorization,
                })
            )
        }
        return contractorAssignment
    }

    handleCompanyAdminMenuClick() {
        this.nodeAdminMenuOpen = true
        if (this.searchUsers.length === 0) {
            this.searchUsersForNodeAdmins()
        }
    }

    get adminIds() {
        return this.nodeAdmins.map((admin) => admin.id)
    }

    get hasFiltersApplied() {
        return (
            Boolean(this.companyList.riskScores.length) ||
            this.companyList.includeArchive ||
            this.companyList.hasUnreadWorkAlert
        )
    }

    async addOrRemoveAdmin(user: any) {
        if (!user.canEdit || !this.hasDelegateAccess) {
            return
        }

        const index = this.nodeAdmins.findIndex((u) => u.id === user.id)
        const isAdding = index === -1

        // https://authorization.api.vbit.com/spec/#tag/projectUserContractorAssignment/paths/~1projectUserContractorAssignments/post
        if (
            isAdding &&
            this.hasDelegateAccess &&
            !this.hasManageAccess &&
            !user.availableContractorsForAssignment.data.find(
                (item) => item.id === this.selectedCompany.id
            )
        ) {
            return
        }

        const contractorAssignment = await this.updateNodeAdmin(user, isAdding)

        if (isAdding) {
            user.contractorAssignments.data.push(contractorAssignment)
            this.nodeAdmins.unshift(user)
        } else {
            this.nodeAdmins.splice(index, 1)
        }
    }

    @Watch('searchTerm')
    @Debounce(500)
    debouncedSearchUsers(value: string) {
        const params = {}
        if (value.length >= 2) {
            params['filter[searchTerm]'] = value
        }
        this.searchUsersForNodeAdmins(params)
    }

    async searchUsersForNodeAdmins(additionalFilters: Record<string, string> = {}) {
        // super user can search all users
        if (this.searchTerm?.length === 1 || !this.hasDelegateAccess) return

        try {
            this.isSearchingProjectUsers = true
            const params = {
                ...additionalFilters,
                'filter[userKindIfConfirmed]': 'normal',
                'filter[project]': this.currentProjectId,
                include:
                    'user,contractors,displayContractor,displayContractor.company,availableContractorsForAssignment,contractorAssignments,contractorAssignments.contractor,contractorAssignments.roleAssignments,contractorAssignments.roleAssignments.role',
            }
            let response = await getRequest(`${this.url_authorization}/projectUsers`, {}, params)
            this.searchUsers = deserialise(response.data).data
        } finally {
            this.isSearchingProjectUsers = false
        }
    }

    contractorRolesDialogOpen = false
    nodeAdminMenuOpen = false
    async showContractorRolesDialog(user: any) {
        this.contractorRolesDialogOpen = true
        this.nodeAdminMenuOpen = false

        const contractorRoles = this.roles.filter((role) => {
            if (role.kind === SystemContractorAdminRole) return true
            if (!role.isContractorRole) return false

            return role.permissions.some((permission) =>
                Boolean(this.rolePermissions[permission]?.isContractorLevel)
            )
        })

        const hasChanged = await openDialog(ContractorRolesDialog, {
            roles: contractorRoles,
            contractor: this.selectedCompany,
            user,
        })
        if (hasChanged) this.getNodeAdmins()

        this.contractorRolesDialogOpen = false
        this.nodeAdminMenuOpen = true
    }

    /**
     * Search for contractor users
     * @param filters filters for API
     * @param includeAll flag to include all users (skip permission check)
     */
    async searchContractorUsers(filters: Record<string, string>, includeAll = false) {
        const params = {
            ...filters,
            'filter[project]': this.currentProjectId,
            'filter[hasRoles]': true,
            include: 'user,roles,roles.permissions,roles.includedPermissions',
        }

        let { data } = await getRequest(`${this.url_authorization}/projectUsers`, {}, params)
        let allUsers = deserialise(data)

        if (includeAll) return allUsers.data

        return allUsers.data.filter((user) => {
            if (!user.user.data) return false

            let permissions = user.roles.data.reduce(
                (permissionList, role) => [
                    ...permissionList,
                    ...role.permissions.data.filter((p) => p.id.includes('contractor.')),
                    ...role.includedPermissions.data.filter((p) => p.id.includes('contractor.')),
                ],
                []
            )

            return permissions.length > 0
        })
    }

    @Debounce(500)
    async getUnreadWorkAlert() {
        if (!this.isPersonnelListModuleActive) return

        const { data } = await getRequest(
            this.getURL({
                path: '/getEntityIds',
                base: this.logURL,
                params: {
                    'filter[isRead]': false,
                    'filter[activityLogEntityId]': this.currentProjectId,
                    'filter[activityLogNumber]': 32,
                },
            })
        )

        if (data?.entityIds && Array.isArray(data.entityIds)) {
            this.companyUnreadWorkAlert = data.entityIds.reduce((map, id) => {
                map[id] = true
                return map
            }, {})
        } else {
            this.companyUnreadWorkAlert = {}
        }
    }

    async getProjectSettings() {
        const { data } = await getRequest(
            this.getURL({
                path: `/projectSettings/${this.currentProjectId}`,
                base: this.contractorBaseUrl,
            })
        )
        this.projectSettings = deserialise(data).data
    }

    riskAssessmentForms = []
    intakeForms = []

    async getContractorForms() {
        const baseParams = {
            'filter[project]': this.currentProjectId,
            'filter[isDeleted]': false,
            'filter[isArchived]': false,
            'filter[hasActiveRevision]': true,
            'page[limit]': 100,
            include: 'availableResponseCompanies',
        }

        const response = await Promise.all([
            getRequest(
                this.getURL({
                    path: 'contractorForms',
                    base: this.contractorBaseUrl,
                    params: {
                        'filter[kind]': 'riskAssessment',
                        ...baseParams,
                    },
                })
            ),
            getRequest(
                this.getURL({
                    path: 'contractorForms',
                    base: this.contractorBaseUrl,
                    params: {
                        'filter[kind]': 'intakeForm,selfReport',
                        ...baseParams,
                    },
                })
            ),
        ])
        this.riskAssessmentForms = deserialise(response[0].data).data
        this.intakeForms = deserialise(response[1].data).data
    }

    filteredContractorRoles: ContractorRole[] = []
    removeFilteredContractorRole(role: ContractorRole) {
        this.filteredContractorRoles = this.filteredContractorRoles.filter(
            (item) => item.id !== role.id
        )
    }

    async removeRole(role: ContractorRole) {
        const roles = this.selectedCompany.roles.data.filter((item) => item.id !== role.id)
        await this.updateCompany({
            id: this.selectedCompany.id,
            roles: {
                data: roles,
            },
        })

        this.selectedCompany.roles.data = roles
    }

    async assignRole(role: ContractorRole) {
        this.updateCompany({
            id: this.selectedCompany.id,
            roles: {
                data: [
                    ...this.selectedCompany.roles.data,
                    {
                        id: role.id,
                        type: 'contractorRole',
                    },
                ],
            },
        })
    }

    async getContractorRoles() {
        const projectHierarchyResponse = await getRequest(
            getURL({
                path: `/contractorHierarchies/${this.currentProjectId}`,
                base: this.contractorBaseUrl,
                params: {
                    include: 'roles',
                },
            })
        )
        this.contractorRoles = deserialise(projectHierarchyResponse.data).data.roles.data
    }

    moreMenuActivator: HTMLElement | null = null
    moreMenuOpen = false
    showMoreMenu(el: HTMLElement) {
        this.moreMenuActivator = el
        this.moreMenuOpen = false
        setTimeout(() => {
            this.moreMenuOpen = true
        })
    }

    addContractorMenu = false
    contractorMenuActivator: HTMLElement | null = null
    showAutocompleteContractor(el: HTMLElement) {
        this.contractorMenuActivator = el
        this.moreMenuOpen = false
        setTimeout(() => {
            this.addContractorMenu = true
        })
    }

    async mounted() {
        this.accessible = this.isModuleViewAccessible('contractor.')

        if (!this.accessible) {
            return
        }

        this.contactList.formRules.name = [
            (v) => !!v || this.$t('fe_validation.error_required').toString(),
        ]
        this.contactList.formRules.description = [
            (v) => !v || v.length <= this.contactDescriptionMaxLength,
        ]
        this.contactList.formRules.email = [
            (v) =>
                !v || /.+@.+\..+/.test(v) || this.$t('fe_validation.error_valid_email').toString(),
        ]

        this.documentList.formRules.name = [
            (v) => !!v || this.$t('fe_validation.error_required').toString(),
            (v) => !v || v.length <= this.documentNameMaxLength,
        ]
        this.documentList.formRules.file = [(v) => (Array.isArray(v) ? v.length > 0 : !!v)]

        this.companyDetails.formRules.endDate = [
            (value: Maybe<string>) => {
                const startDate = this.selectedCompany.companyDetails.data.startDate
                if (!value || !startDate) return true
                // @ts-ignore
                return this.isoDate(value) >= this.isoDate(startDate)
            },
        ]

        this.getCompanyHierarchy()
        this.getProjectSettings()
        this.getUnreadWorkAlert()
        this.getContractorForms()
        this.getContractorRoles()
    }
}
